import { useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import {
  ProConfigProvider,
  enUSIntl,
  zhCNIntl,
} from '@ant-design/pro-components';
import { App as AntApp, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { locale } from '@/lib/i18n';
import { useAppSettings } from './app-settings';
import { createLobeTheme } from './lobe-theme';

const antdLocale = locale === 'en-US' ? enUS : zhCN;
const proIntl = locale === 'en-US' ? enUSIntl : zhCNIntl;

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const { settings } = useAppSettings();
  const { themeMode, primaryColor, compact } = settings;

  const theme = useMemo(
    () => createLobeTheme({ themeMode, primaryColor, compact }),
    [themeMode, primaryColor, compact],
  );

  // Static `message` calls mount outside React's provider tree. Keep their host themed too.
  useEffect(() => {
    ConfigProvider.config({
      holderRender: (content) => (
        <ConfigProvider locale={antdLocale} theme={theme}>
          {content}
        </ConfigProvider>
      ),
    });
    return () => ConfigProvider.config({ holderRender: undefined });
  }, [theme]);

  // The document itself has to agree with antd: `color-scheme` is what makes scrollbars,
  // form controls and the browser's own surfaces dark, and the attribute is what the
  // stylesheet hooks onto for the page background.
  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
  }, [themeMode]);

  return (
    <ConfigProvider locale={antdLocale} theme={theme}>
      <ProConfigProvider dark={themeMode === 'dark'} intl={proIntl}>
        <AntApp>{children}</AntApp>
      </ProConfigProvider>
    </ConfigProvider>
  );
};
